import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserPlus, KeyRound, Trash2, ShieldCheck, X, Check, Pencil } from "lucide-react";

export default function AdminTeam() {
  const { user } = useAuth();
  const [tab, setTab] = useState("team");
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const isAdmin = user?.role === "admin";

  const load = () => {
    if (!isAdmin) return;
    api.get("/users").then((r) => setUsers(r.data)).catch((e) => {
      if (e.response?.status === 403) toast.error("admin role required.");
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isAdmin]);

  return (
    <AdminLayout>
      <div data-testid="admin-team" className="max-w-5xl">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="editorial-label">team · access</span>
            <h1 className="font-serif text-5xl lowercase text-charcoal mt-3">who tends the atelier.</h1>
          </div>
        </div>

        <div className="flex gap-8 border-b border-taupe/50 mb-10">
          <button onClick={() => setTab("team")} data-testid="tab-team-members" className={`pb-4 text-xs tracking-[0.25em] uppercase border-b-2 ${tab === "team" ? "border-brass text-brass" : "border-transparent text-charcoal hover:text-brass"}`}>team members</button>
          <button onClick={() => setTab("account")} data-testid="tab-my-account" className={`pb-4 text-xs tracking-[0.25em] uppercase border-b-2 ${tab === "account" ? "border-brass text-brass" : "border-transparent text-charcoal hover:text-brass"}`}>my account</button>
        </div>

        {tab === "team" && (
          <section data-testid="team-section">
            {!isAdmin ? (
              <div className="bg-linen border border-taupe/50 p-10 text-center" data-testid="admin-only-panel">
                <ShieldCheck className="w-8 h-8 text-taupe mx-auto mb-4" />
                <p className="font-serif text-2xl lowercase text-charcoal">admin role required.</p>
                <p className="editorial-label mt-3">ask an existing admin to manage team members.</p>
              </div>
            ) : (
              <>
                <div className="flex items-end justify-between mb-6">
                  <p className="text-sm text-espresso max-w-xl">invite trusted staff to manage inventory & enquiries, or grant a fellow admin full access.</p>
                  <button onClick={() => setShowCreate(true)} data-testid="add-user-btn" className="bg-brass hover:bg-espresso text-ivory px-5 py-3 text-xs tracking-[0.25em] uppercase flex items-center gap-2">
                    <UserPlus className="w-4 h-4" /> invite member
                  </button>
                </div>

                <div className="overflow-x-auto border border-taupe/40">
                  <table className="w-full text-sm">
                    <thead className="bg-linen">
                      <tr className="text-left">
                        {["member", "email", "role", "status", "joined", ""].map((h) => (
                          <th key={h} className="editorial-label px-5 py-4 border-b border-taupe/50">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u, i) => (
                        <UserRow key={u.id} u={u} i={i} self={u.id === user?.id} onChange={load} onReset={() => setResetTarget(u)} onEdit={() => setEditTarget(u)} />
                      ))}
                      {users.length === 0 && (
                        <tr><td colSpan="6" className="text-center py-12 font-serif text-2xl lowercase text-taupe">no members yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        )}

        {tab === "account" && <MyAccount />}

        {showCreate && <CreateUserDialog onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
        {resetTarget && <ResetPasswordDialog target={resetTarget} onClose={() => setResetTarget(null)} />}
        {editTarget && <EditUserDialog target={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); load(); }} />}
      </div>
    </AdminLayout>
  );
}

function UserRow({ u, i, self, onChange, onReset, onEdit }) {
  const [busy, setBusy] = useState(false);

  const toggleActive = async () => {
    setBusy(true);
    try {
      await api.put(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "member deactivated." : "member activated.");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "update failed.");
    } finally { setBusy(false); }
  };

  const changeRole = async (newRole) => {
    setBusy(true);
    try {
      await api.put(`/users/${u.id}`, { role: newRole });
      toast.success("role updated.");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "update failed.");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`remove ${u.email} from the team?`)) return;
    setBusy(true);
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("member removed.");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "delete failed.");
    } finally { setBusy(false); }
  };

  return (
    <tr className="border-b border-taupe/30 hover:bg-linen/50" data-testid={`user-row-${i}`}>
      <td className="px-5 py-4">
        <span className="font-serif text-lg lowercase text-charcoal">{u.name?.toLowerCase()}</span>
        {self && <span className="ml-2 text-[0.6rem] tracking-widest uppercase text-brass">· you</span>}
      </td>
      <td className="px-5 py-4 text-charcoal">{u.email}</td>
      <td className="px-5 py-4">
        <select value={u.role} disabled={busy || self} onChange={(e) => changeRole(e.target.value)} data-testid={`role-select-${i}`} className="bg-transparent border-b border-taupe py-1 focus:outline-none text-charcoal text-xs">
          <option value="admin">admin</option>
          <option value="staff">staff</option>
        </select>
      </td>
      <td className="px-5 py-4">
        <button onClick={toggleActive} disabled={busy || self} data-testid={`active-toggle-${i}`} className={`text-[0.65rem] tracking-widest uppercase px-3 py-1 border ${u.is_active ? "border-brass text-brass" : "border-taupe text-taupe"}`}>
          {u.is_active ? "active" : "paused"}
        </button>
      </td>
      <td className="px-5 py-4 text-xs text-taupe">{new Date(u.created_at).toLocaleDateString()}</td>
      <td className="px-5 py-4 text-right space-x-3">
        <button onClick={onEdit} disabled={busy} data-testid={`edit-user-${i}`} title="edit details" className="text-taupe hover:text-brass"><Pencil className="w-4 h-4 inline" /></button>
        <button onClick={onReset} disabled={busy} data-testid={`reset-pwd-${i}`} title="reset password" className="text-taupe hover:text-brass"><KeyRound className="w-4 h-4 inline" /></button>
        {!self && (
          <button onClick={remove} disabled={busy} data-testid={`delete-user-${i}`} title="remove" className="text-taupe hover:text-red-700"><Trash2 className="w-4 h-4 inline" /></button>
        )}
      </td>
    </tr>
  );
}

function MyAccount() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [twoFA, setTwoFA] = useState(null);
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpCode, setTotpCode] = useState("");

  useEffect(() => {
    api.get("/auth/me").then((r) => setTwoFA({ totp: r.data.totp_enabled, email: r.data.email_otp_enabled }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("new password must be at least 8 characters.");
    if (next !== confirm) return toast.error("new passwords don't match.");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("password updated. use it next time you sign in.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not update password.");
    } finally { setSaving(false); }
  };

  const startTotpSetup = async () => {
    const { data } = await api.post("/auth/2fa/totp/setup");
    setTotpSetup(data);
  };
  const enableTotp = async () => {
    try {
      await api.post("/auth/2fa/totp/enable", { code: totpCode });
      toast.success("totp 2fa enabled. scan saved.");
      setTotpSetup(null); setTotpCode("");
      setTwoFA({ ...twoFA, totp: true });
    } catch (e) { toast.error(e.response?.data?.detail || "invalid code."); }
  };
  const disableTotp = async () => {
    const code = window.prompt("enter your current 6-digit totp code to disable:");
    if (!code) return;
    try {
      await api.post("/auth/2fa/totp/disable", { code });
      toast.success("totp 2fa disabled.");
      setTwoFA({ ...twoFA, totp: false });
    } catch (e) { toast.error(e.response?.data?.detail || "invalid code."); }
  };
  const toggleEmailOtp = async () => {
    const enabled = !twoFA?.email;
    await api.post("/auth/2fa/email-otp", { enabled });
    toast.success(`email otp ${enabled ? "enabled" : "disabled"}.`);
    setTwoFA({ ...twoFA, email: enabled });
  };

  return (
    <section className="bg-linen border border-taupe/50 p-8 max-w-2xl" data-testid="my-account-section">
      <h2 className="font-serif text-2xl lowercase text-charcoal mb-1">{user?.name?.toLowerCase()}</h2>
      <p className="text-xs text-espresso mb-8">{user?.email} · {user?.role}</p>

      <form onSubmit={submit} className="space-y-5 mb-12">
        <p className="editorial-label">change studio password</p>
        <div>
          <label className="editorial-label">current password</label>
          <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="pwd-current" className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" />
        </div>
        <div>
          <label className="editorial-label">new password (min 8 chars)</label>
          <input type="password" required value={next} onChange={(e) => setNext(e.target.value)} data-testid="pwd-new" className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" />
        </div>
        <div>
          <label className="editorial-label">confirm new password</label>
          <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="pwd-confirm" className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" />
        </div>
        <button type="submit" disabled={saving} data-testid="pwd-submit" className="bg-brass hover:bg-espresso text-ivory px-8 py-3 text-xs tracking-[0.25em] uppercase disabled:opacity-50">
          {saving ? "updating…" : "update password"}
        </button>
      </form>

      <div className="border-t border-taupe/40 pt-8" data-testid="twofa-section">
        <p className="editorial-label mb-4">two-factor authentication</p>

        <div className="space-y-4">
          <div className="flex items-center justify-between border border-taupe/40 bg-ivory p-4">
            <div>
              <p className="font-serif text-lg lowercase text-charcoal">authenticator app · totp</p>
              <p className="text-xs text-espresso mt-1">most secure. uses google authenticator, authy, 1password, etc.</p>
            </div>
            {twoFA?.totp ? (
              <button onClick={disableTotp} data-testid="totp-disable" className="border border-red-700 text-red-700 hover:bg-red-700 hover:text-ivory px-4 py-2 text-[0.65rem] tracking-[0.25em] uppercase">disable</button>
            ) : (
              <button onClick={startTotpSetup} data-testid="totp-setup" className="bg-brass hover:bg-espresso text-ivory px-4 py-2 text-[0.65rem] tracking-[0.25em] uppercase">set up</button>
            )}
          </div>

          {totpSetup && (
            <div className="bg-ivory border border-brass p-6" data-testid="totp-setup-panel">
              <p className="font-serif text-xl lowercase text-charcoal mb-4">1 · scan this qr in your authenticator app</p>
              <img alt="totp qr" src={`data:image/png;base64,${totpSetup.qr_png_base64}`} className="w-48 h-48 border border-taupe/40 mb-4" />
              <p className="text-[0.65rem] text-taupe tracking-widest uppercase mb-2">or paste this secret · {totpSetup.secret}</p>
              <p className="font-serif text-xl lowercase text-charcoal mt-6 mb-2">2 · enter the 6-digit code your app shows</p>
              <div className="flex gap-3">
                <input type="text" inputMode="numeric" maxLength={6} value={totpCode} onChange={(e) => setTotpCode(e.target.value)} data-testid="totp-code" className="bg-transparent border-b border-taupe py-2 px-2 focus:outline-none focus:border-brass text-charcoal tracking-widest" />
                <button onClick={enableTotp} data-testid="totp-enable" className="bg-brass hover:bg-espresso text-ivory px-4 py-2 text-[0.65rem] tracking-[0.25em] uppercase">enable totp</button>
                <button onClick={() => setTotpSetup(null)} className="border border-taupe text-charcoal px-4 py-2 text-[0.65rem] tracking-[0.25em] uppercase">cancel</button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between border border-taupe/40 bg-ivory p-4">
            <div>
              <p className="font-serif text-lg lowercase text-charcoal">email one-time code</p>
              <p className="text-xs text-espresso mt-1">a 6-digit code emailed at every sign-in. simpler, slightly less secure.</p>
            </div>
            <button onClick={toggleEmailOtp} data-testid="email-otp-toggle" className={`px-4 py-2 text-[0.65rem] tracking-[0.25em] uppercase ${twoFA?.email ? "border border-red-700 text-red-700 hover:bg-red-700 hover:text-ivory" : "bg-brass hover:bg-espresso text-ivory"}`}>
              {twoFA?.email ? "disable" : "enable"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function CreateUserDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("staff");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("password must be at least 8 characters.");
    setSaving(true);
    try {
      await api.post("/users", { name, email, password, role });
      toast.success(`${name} invited.`);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not invite member.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose} data-testid="create-user-dialog">
      <form onSubmit={submit} className="bg-ivory border border-taupe max-w-md w-full p-10 relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-taupe hover:text-charcoal" data-testid="create-user-close"><X className="w-5 h-5" /></button>
        <span className="editorial-label">team · invite</span>
        <h2 className="font-serif text-3xl lowercase text-charcoal mt-3 mb-6">welcome a new hand.</h2>
        <div className="space-y-5">
          <div><label className="editorial-label">full name</label><input required value={name} onChange={(e) => setName(e.target.value)} data-testid="new-user-name" className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" /></div>
          <div><label className="editorial-label">email</label><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="new-user-email" className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" /></div>
          <div><label className="editorial-label">temporary password</label><input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="new-user-password" className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" /><p className="text-[0.6rem] text-taupe mt-1 tracking-widest uppercase">min 8 chars</p></div>
          <div>
            <label className="editorial-label">role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="new-user-role" className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none text-charcoal">
              <option value="staff">staff · inventory & enquiries</option>
              <option value="admin">admin · full access</option>
            </select>
          </div>
        </div>
        <button type="submit" disabled={saving} data-testid="new-user-submit" className="w-full mt-8 bg-brass hover:bg-espresso text-ivory py-3 text-xs tracking-[0.25em] uppercase disabled:opacity-50 flex items-center justify-center gap-2">
          <Check className="w-4 h-4" /> {saving ? "inviting…" : "send invitation"}
        </button>
      </form>
    </div>
  );
}

function EditUserDialog({ target, onClose, onSaved }) {
  const [name, setName] = useState(target.name || "");
  const [role, setRole] = useState(target.role || "staff");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("name is required.");
    setSaving(true);
    try {
      await api.put(`/users/${target.id}`, { name: name.trim(), role });
      toast.success("member updated.");
      onSaved();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "could not update.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose} data-testid="edit-user-dialog">
      <form onSubmit={submit} className="bg-ivory border border-taupe max-w-md w-full p-10 relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-taupe hover:text-charcoal"><X className="w-5 h-5" /></button>
        <span className="editorial-label">team · edit</span>
        <h2 className="font-serif text-3xl lowercase text-charcoal mt-3 mb-1">{target.name?.toLowerCase()}</h2>
        <p className="text-xs text-espresso mb-6">email is locked to preserve the audit trail · use &ldquo;reset password&rdquo; to change credentials</p>
        <div className="space-y-5">
          <div>
            <label className="editorial-label">full name</label>
            <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-user-name" className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" />
          </div>
          <div>
            <label className="editorial-label">email (read-only)</label>
            <input value={target.email} disabled className="w-full bg-transparent border-b border-taupe/40 py-2 mt-1 text-taupe cursor-not-allowed" />
          </div>
          <div>
            <label className="editorial-label">role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="edit-user-role" className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none text-charcoal">
              <option value="staff">staff · inventory & enquiries</option>
              <option value="admin">admin · full access</option>
            </select>
          </div>
        </div>
        <button type="submit" disabled={saving} data-testid="edit-user-submit" className="w-full mt-8 bg-brass hover:bg-espresso text-ivory py-3 text-xs tracking-[0.25em] uppercase disabled:opacity-50 flex items-center justify-center gap-2">
          <Check className="w-4 h-4" /> {saving ? "saving…" : "save changes"}
        </button>
      </form>
    </div>
  );
}


function ResetPasswordDialog({ target, onClose }) {
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) return toast.error("password must be at least 8 characters.");
    setSaving(true);
    try {
      await api.post(`/users/${target.id}/reset-password`, { new_password: pwd });
      toast.success(`password reset for ${target.email}.`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "reset failed.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose} data-testid="reset-pwd-dialog">
      <form onSubmit={submit} className="bg-ivory border border-taupe max-w-md w-full p-10 relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-taupe hover:text-charcoal"><X className="w-5 h-5" /></button>
        <span className="editorial-label">reset · password</span>
        <h2 className="font-serif text-3xl lowercase text-charcoal mt-3 mb-2">{target.name?.toLowerCase()}</h2>
        <p className="text-xs text-espresso mb-6">share this new password with them privately.</p>
        <label className="editorial-label">new password (min 8 chars)</label>
        <input type="text" required autoFocus value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="reset-pwd-input" className="w-full bg-transparent border-b border-taupe py-2 mt-1 focus:outline-none focus:border-brass text-charcoal" />
        <button type="submit" disabled={saving} data-testid="reset-pwd-submit" className="w-full mt-8 bg-brass hover:bg-espresso text-ivory py-3 text-xs tracking-[0.25em] uppercase disabled:opacity-50">
          {saving ? "resetting…" : "reset password"}
        </button>
      </form>
    </div>
  );
}
